import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { translateText } from '../services/translationService';

type FlatMap = Map<string, string>;

const normalize = (value: string): string => value.replace(/\s+/g, ' ').trim().toLowerCase();

const shouldSkipText = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (trimmed.length > 220) return true;
  if (/^[-–—_=*]+$/.test(trimmed)) return true;
  if (/^https?:\/\//i.test(trimmed)) return true;
  if (/\bwww\./i.test(trimmed)) return true;
  if (/\S+@\S+\.\S+/.test(trimmed)) return true;
  if (/^[0-9\s.,:+-]+$/.test(trimmed)) return true;
  return false;
};

const flattenStrings = (input: unknown, prefix = '', out: FlatMap = new Map()): FlatMap => {
  if (!input || typeof input !== 'object') return out;
  Object.entries(input as Record<string, unknown>).forEach(([key, value]) => {
    const nextKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string') {
      out.set(nextKey, value);
      return;
    }
    if (value && typeof value === 'object') {
      flattenStrings(value, nextKey, out);
    }
  });
  return out;
};

const buildValueToKeyIndex = (flat: FlatMap): Map<string, string> => {
  const index = new Map<string, string>();
  for (const [key, value] of flat.entries()) {
    const norm = normalize(value);
    if (!norm) continue;
    if (!index.has(norm)) index.set(norm, key);
  }
  return index;
};

const getTextNodes = (root: Node): Text[] => {
  const nodes: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      const tag = parent.tagName?.toLowerCase();
      if (!tag) return NodeFilter.FILTER_REJECT;
      if (['script', 'style', 'noscript', 'pre', 'code', 'textarea', 'input', 'select', 'option'].includes(tag)) {
        return NodeFilter.FILTER_REJECT;
      }
      if (parent.closest('[data-no-auto-translate="true"]')) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  let current: Node | null = walker.nextNode();
  while (current) {
    nodes.push(current as Text);
    current = walker.nextNode();
  }
  return nodes;
};

const getLocalCacheKey = (lng: string) => `haapa.autoDomTranslations.${lng}`;

const readCache = (lng: string): Record<string, string> => {
  try {
    const raw = window.localStorage.getItem(getLocalCacheKey(lng));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Record<string, string>;
  } catch {
    return {};
  }
};

const writeCache = (lng: string, next: Record<string, string>) => {
  try {
    window.localStorage.setItem(getLocalCacheKey(lng), JSON.stringify(next));
  } catch {
    // ignore
  }
};

const withConcurrency = async <T,>(
  tasks: Array<() => Promise<T>>,
  concurrency: number,
): Promise<T[]> => {
  const results: T[] = [];
  let index = 0;

  const runOne = async () => {
    while (index < tasks.length) {
      const currentIndex = index;
      index += 1;
      results[currentIndex] = await tasks[currentIndex]();
    }
  };

  await Promise.all(Array.from({ length: Math.max(1, concurrency) }, runOne));
  return results;
};

export default function AutoDomTranslator() {
  const { i18n } = useTranslation();
  const originalTextRef = useRef<WeakMap<Text, string>>(new WeakMap());
  const pendingRef = useRef<Set<string>>(new Set());
  const cacheRef = useRef<Record<string, string> | null>(null);
  const cacheLngRef = useRef<string>('');
  const flushTimerRef = useRef<number | null>(null);

  const valueToKeyIndex = useMemo(() => {
    try {
      const bundle = i18n.getResourceBundle('en', 'translation');
      const flat = flattenStrings(bundle);
      return buildValueToKeyIndex(flat);
    } catch {
      return new Map<string, string>();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const lng = (i18n.resolvedLanguage ?? i18n.language ?? 'en').toLowerCase();
    const isEnglish = lng.startsWith('en');

    if (!cacheRef.current || cacheLngRef.current !== lng) {
      cacheRef.current = readCache(lng);
      cacheLngRef.current = lng;
    }

    const hasTranslationForLanguage = (key: string) => {
      try {
        return i18n.exists(key, { lng, ns: 'translation', fallbackLng: false });
      } catch {
        return false;
      }
    };

    const applyTranslationToNode = async (node: Text) => {
      const currentText = node.textContent ?? '';
      const storedOriginal = originalTextRef.current.get(node);
      const originalText = storedOriginal ?? currentText;
      if (!storedOriginal) {
        originalTextRef.current.set(node, originalText);
      }

      const match = originalText.match(/^(\s*)(.*?)(\s*)$/s);
      const leading = match?.[1] ?? '';
      const core = match?.[2] ?? originalText;
      const trailing = match?.[3] ?? '';

      if (shouldSkipText(core)) return;

      if (isEnglish) {
        const next = `${leading}${core}${trailing}`;
        if (node.textContent !== next) node.textContent = next;
        return;
      }

      const norm = normalize(core);
      const key = valueToKeyIndex.get(norm);
      if (key && hasTranslationForLanguage(key)) {
        const translated = i18n.t(key, { lng, defaultValue: core });
        const next = `${leading}${translated}${trailing}`;
        if (node.textContent !== next) node.textContent = next;
        return;
      }

      const cached = cacheRef.current?.[norm];
      if (cached) {
        const next = `${leading}${cached}${trailing}`;
        if (node.textContent !== next) node.textContent = next;
        return;
      }

      // API fallback for strings not present in locale files (or missing in the selected language).
      const requestKey = `${lng}:${norm}`;
      if (pendingRef.current.has(requestKey)) return;
      pendingRef.current.add(requestKey);
      try {
        const sourceText = key ? i18n.t(key, { lng: 'en', defaultValue: core }) : core;
        const translated = await translateText(sourceText, lng, 'en');
        const safe = translated?.trim() ? translated : sourceText;
        const next = `${leading}${safe}${trailing}`;
        if (node.textContent !== next) node.textContent = next;

        const cache = cacheRef.current ?? {};
        cache[norm] = safe;
        cacheRef.current = cache;
        writeCache(lng, cache);

        if (key) {
          try {
            i18n.addResource(lng, 'translation', key, safe, { silent: true });
            i18n.emit('added', lng, 'translation', key);
          } catch {
            // ignore
          }
        }
      } finally {
        pendingRef.current.delete(requestKey);
      }
    };

    const flush = () => {
      if (flushTimerRef.current) {
        window.clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }

      const nodes = getTextNodes(document.body);
      // Build tasks with captured nodes to avoid long sync work.
      const nodeTasks = nodes.map((node) => async () => {
        await applyTranslationToNode(node);
      });
      void withConcurrency(nodeTasks, 6);
    };

    const scheduleFlush = () => {
      if (flushTimerRef.current) return;
      flushTimerRef.current = window.setTimeout(flush, 120);
    };

    // Initial pass
    scheduleFlush();

    const observer = new MutationObserver(() => scheduleFlush());
    observer.observe(document.body, { subtree: true, childList: true, characterData: true });

    const handleResourceAdded = (...args: unknown[]) => {
      const addedLng = (args[0] ?? '').toString().toLowerCase();
      const ns = (args[1] ?? '').toString();
      if (addedLng && addedLng !== lng) return;
      if (ns && ns !== 'translation') return;
      scheduleFlush();
    };

    i18n.on('added', handleResourceAdded);
    i18n.on('loaded', scheduleFlush);

    return () => {
      i18n.off('added', handleResourceAdded);
      i18n.off('loaded', scheduleFlush);
      observer.disconnect();
      if (flushTimerRef.current) {
        window.clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
  }, [i18n, i18n.language, i18n.resolvedLanguage, valueToKeyIndex]);

  return null;
}

